// Role-select modal — opens after a centre user (client_id) verifies
// their password. Lets the user choose between continuing as the
// centre admin (existing OTP flow) or logging in as a doctor mapped
// under that centre (new flow → /api/auth/doctor/*).
//
// Patients (UHID login) never see this — the parent component checks
// identifier_type === 'client_id' before opening the modal.
//
// Lifecycle:
//   Step 1 — two CTAs:
//     "Continue as Client"  → onContinueAsClient() — parent handles
//                             the existing OTP flow, modal closes.
//     "Login as Doctor"     → switches the modal to step 2.
//   Step 2 — phone input + Send OTP:
//     On submit we call /api/auth/doctor/send-otp, then call
//     onDoctorOtpSent({ phone, masked_phone, doctor_name }) so the
//     parent can switch its OTP screen into doctor-verify mode.

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { useSendDoctorOtpMutation } from '../../store/authApi';

const RoleSelectModal = ({
  open,
  clientId,            // The client_id the user typed on the login form
  centreName,          // Friendly centre name (e.g. "Begumpet Diagnostics")
  onContinueAsClient,  // ()
  onDoctorOtpSent,     // ({ phone, masked_phone, doctor_name })
  onClose,             // ()
}) => {
  const [step, setStep] = useState('choose');  // 'choose' | 'phone'
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const [sendDoctorOtp, { isLoading }] = useSendDoctorOtpMutation();

  const reset = () => {
    setStep('choose');
    setPhone('');
    setErr('');
  };

  const handleClose = () => {
    if (isLoading) return;
    reset();
    onClose?.();
  };

  const onPickClient = () => {
    reset();
    onContinueAsClient?.();
  };

  const onPickDoctor = () => {
    setErr('');
    setStep('phone');
  };

  const onPhoneSubmit = async (e) => {
    e?.preventDefault?.();
    setErr('');
    if (!/^\d{10}$/.test(phone.trim())) {
      setErr('Enter a valid 10-digit mobile number');
      return;
    }
    try {
      const res = await sendDoctorOtp({
        client_id: clientId,
        phone: phone.trim(),
      }).unwrap();
      onDoctorOtpSent?.({
        phone: phone.trim(),
        masked_phone: res?.masked_phone || '',
        doctor_name: res?.doctor_name || '',
      });
      reset();
    } catch (e2) {
      setErr(e2?.data?.message || 'Could not send OTP. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 3.5, py: 2.25, borderBottom: '1px solid #e6f0ed', bgcolor: '#F4FAF9' }}
        >
          {step === 'phone' && (
            <IconButton onClick={() => { setStep('choose'); setErr(''); }} sx={{ mr: 1.25 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography sx={{ flex: 1, fontWeight: 800, fontSize: 18, color: '#1A3A2A', letterSpacing: '-0.01em' }}>
            {step === 'choose' ? 'How are you signing in?' : 'Login as Doctor'}
          </Typography>
          <IconButton onClick={handleClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Step 1 — role picker */}
        {step === 'choose' && (
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Centre context — so the user is never confused about
                which client they're logging into. */}
            {(centreName || clientId) && (
              <Box sx={{
                mb: 3, px: 2.25, py: 1.75, borderRadius: 2,
                bgcolor: '#E6F7F5', border: '1px solid #B3E8E2',
              }}>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: '#009982', mb: 0.5,
                }}>
                  Signing in to
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1A3A2A', lineHeight: 1.25 }}>
                  {centreName || clientId}
                </Typography>
                {centreName && clientId && (
                  <Typography sx={{ fontSize: 12, color: '#6B8A7F', mt: 0.25 }}>
                    Client ID: <strong>{clientId}</strong>
                  </Typography>
                )}
              </Box>
            )}

            <Typography sx={{ fontSize: 15, color: '#2D5040', mb: 3, lineHeight: 1.55 }}>
              Centre admin or a doctor at this centre? Pick one to continue —
              both paths go through an OTP check.
            </Typography>

            <Stack spacing={2}>
              {/* Continue as Client */}
              <Button
                fullWidth
                variant="contained"
                onClick={onPickClient}
                sx={{
                  py: 2.5, px: 2.5,
                  justifyContent: 'flex-start',
                  bgcolor: '#00B49A', color: '#1A3A2A',
                  textTransform: 'none',
                  borderRadius: 2, boxShadow: 'none',
                  '&:hover': { bgcolor: '#009982', color: '#fff' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.25)',
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <BusinessOutlinedIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    <Box sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                      Continue as Client
                    </Box>
                    <Box sx={{ fontSize: 12.5, fontWeight: 500, opacity: 0.9, mt: 0.4 }}>
                      OTP to the centre's registered number
                    </Box>
                  </Box>
                </Stack>
              </Button>

              {/* Login as Doctor */}
              <Button
                fullWidth
                variant="outlined"
                onClick={onPickDoctor}
                sx={{
                  py: 2.5, px: 2.5,
                  justifyContent: 'flex-start',
                  borderColor: '#00B49A',
                  borderWidth: 2,
                  color: '#1A3A2A',
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#009982', borderWidth: 2,
                    bgcolor: '#E6F7F5',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2,
                    bgcolor: '#E6F7F5', color: '#009982',
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <LocalHospitalOutlinedIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    <Box sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                      Login as Doctor
                    </Box>
                    <Box sx={{ fontSize: 12.5, fontWeight: 500, color: '#6B8A7F', mt: 0.4 }}>
                      OTP to your personal mobile number
                    </Box>
                  </Box>
                </Stack>
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 2 — doctor phone */}
        {step === 'phone' && (
          <Box component="form" onSubmit={onPhoneSubmit} sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Same centre banner as Step 1 so the doctor stays aware
                of which client they're being matched against. */}
            {(centreName || clientId) && (
              <Box sx={{
                mb: 3, px: 2.25, py: 1.75, borderRadius: 2,
                bgcolor: '#E6F7F5', border: '1px solid #B3E8E2',
              }}>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: '#009982', mb: 0.5,
                }}>
                  Doctor at
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1A3A2A', lineHeight: 1.25 }}>
                  {centreName || clientId}
                </Typography>
                {centreName && clientId && (
                  <Typography sx={{ fontSize: 12, color: '#6B8A7F', mt: 0.25 }}>
                    Client ID: <strong>{clientId}</strong>
                  </Typography>
                )}
              </Box>
            )}

            <Typography sx={{ fontSize: 15, color: '#2D5040', mb: 3, lineHeight: 1.55 }}>
              Enter the mobile number registered against your doctor profile here.
              We'll send a 6-digit OTP to that number.
            </Typography>

            <TextField
              fullWidth
              label="Mobile number"
              placeholder="10-digit number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputProps={{ inputMode: 'numeric', autoFocus: true, style: { fontSize: 16, letterSpacing: '0.05em' } }}
              disabled={isLoading}
              sx={{
                mb: 2.5,
                '& .MuiInputBase-root': { py: 0.75 },
              }}
            />

            {err && <Alert severity="error" sx={{ mb: 2.5, fontSize: 13 }}>{err}</Alert>}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading || phone.length !== 10}
              sx={{
                py: 1.75, bgcolor: '#00B49A', color: '#1A3A2A',
                textTransform: 'none', fontWeight: 800, fontSize: 15,
                borderRadius: 2, boxShadow: 'none',
                '&:hover': { bgcolor: '#009982', color: '#fff' },
                '&.Mui-disabled': { bgcolor: '#E6F7F5', color: '#9DBBB2' },
              }}
            >
              {isLoading ? 'Sending OTP…' : 'Send OTP'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoleSelectModal;
